import { renderDateCell } from '@/lib/listCells';
import type { CrudConfig } from '@startupquickstart/frontend';

export type ArtifactType =
  | 'scramble'
  | 'freeze'
  | 'blackout'
  | 'steal'
  | 'misdirect'
  | 'shield'
  | 'double_down';

export type ArtifactCategory = 'offensive' | 'defensive' | 'wildcard';
export type ArtifactClaimMode = 'all_teams' | 'first_claim';
export type ArtifactDistributionMode = 'map_discovery' | 'pre_loaded';

export interface Artifact {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  artifactType: ArtifactType;
  artifactCategory: ArtifactCategory;
  detectionRadius: number;
  claimRadius: number;
  effectDuration?: number | null;
  effectValue?: number | null;
  imageUrl?: string | null;
  gateEnabled: boolean;
  gateClue?: string | null;
  gateAnswer?: string | null;
  targetCount: number;
  claimMode: ArtifactClaimMode;
  /**
   * DT-169. Default distribution mode for this library template.
   * Copied onto event artifacts at add time and overridable per-event.
   */
  distributionMode: ArtifactDistributionMode;
  /** Default uses-per-team for pre_loaded library templates. */
  quantityPerTeam?: number | null;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
  } | null;
}

const artifactTypeLabels: Record<ArtifactType, string> = {
  scramble: 'Scramble',
  freeze: 'Freeze',
  blackout: 'Blackout',
  steal: 'Steal',
  misdirect: 'Misdirect',
  shield: 'Shield',
  double_down: 'Double Down',
};

const artifactCategoryColors: Record<ArtifactCategory, string> = {
  offensive: 'bg-red-100 text-red-700',
  defensive: 'bg-blue-100 text-blue-700',
  wildcard: 'bg-purple-100 text-purple-700',
};

const artifactCategoryLabels: Record<ArtifactCategory, string> = {
  offensive: 'Offensive',
  defensive: 'Defensive',
  wildcard: 'Wildcard',
};

export const artifactCrudConfig: CrudConfig<Artifact> = {
  entityName: 'Artifact',
  entityNamePlural: 'Artifacts',

  endpoints: {
    list: '/artifacts',
    single: (id: string) => `/artifacts/${id}`,
    create: '/artifacts',
    update: (id: string) => `/artifacts/${id}`,
    delete: (id: string) => `/artifacts/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter artifact name',
      validation: {
        min: 1,
        max: 200,
      },
    },
    {
      key: 'artifactType',
      label: 'Type',
      type: 'select',
      required: true,
      options: Object.entries(artifactTypeLabels).map(([value, label]) => ({
        label,
        value,
      })),
      format: (value: string) => {
        if (!value) return null;
        return (
          <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
            {artifactTypeLabels[value as ArtifactType] || value}
          </span>
        );
      },
    },
    {
      key: 'artifactCategory',
      label: 'Category',
      type: 'select',
      required: true,
      options: [
        { label: 'Offensive', value: 'offensive' },
        { label: 'Defensive', value: 'defensive' },
        { label: 'Wildcard', value: 'wildcard' },
      ],
      format: (value: string) => {
        if (!value) return null;
        const category = value as ArtifactCategory;
        return (
          <span
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${artifactCategoryColors[category]}`}
          >
            {artifactCategoryLabels[category]}
          </span>
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Flavor text shown when artifact is claimed',
    },
    {
      key: 'detectionRadius',
      label: 'Detection Radius (m)',
      type: 'number',
      required: true,
      defaultValue: 100,
      helpText: 'Distance in meters when teams get an alert',
    },
    {
      key: 'claimRadius',
      label: 'Claim Radius (m)',
      type: 'number',
      required: true,
      defaultValue: 10,
      helpText: 'Distance in meters when teams can claim',
    },
    {
      key: 'effectDuration',
      label: 'Effect Duration (min)',
      type: 'number',
      required: false,
      helpText: 'Minutes the effect lasts (leave empty for non-timed effects)',
    },
    {
      key: 'effectValue',
      label: 'Effect Value',
      type: 'number',
      required: false,
      helpText: 'Optional numeric value used by some effects (legacy).',
    },
    {
      key: 'imageUrl',
      label: 'Image',
      type: 'text',
      required: false,
      placeholder: 'Enter image URL',
    },
    {
      key: 'gateEnabled',
      label: 'Gate Challenge',
      type: 'boolean',
      required: false,
      defaultValue: false,
      helpText: 'Require answering a question before claiming',
    },
    {
      key: 'gateClue',
      label: 'Gate Clue',
      type: 'richtext',
      required: false,
      placeholder: 'The question teams must answer to claim',
    },
    {
      key: 'gateAnswer',
      label: 'Gate Answer',
      type: 'text',
      required: false,
      placeholder: 'Expected answer',
    },
    {
      key: 'targetCount',
      label: 'Target Count',
      type: 'number',
      required: true,
      defaultValue: 1,
      helpText: 'Number of teams to target (0 = all teams)',
    },
    {
      key: 'claimMode',
      label: 'Claim Mode',
      type: 'select',
      required: true,
      defaultValue: 'all_teams',
      options: [
        { label: 'All Teams', value: 'all_teams' },
        { label: 'First Claim Only', value: 'first_claim' },
      ],
      format: (value: string) => {
        const labels: Record<string, string> = {
          all_teams: 'All Teams',
          first_claim: 'First Claim',
        };
        return labels[value] || value;
      },
    },
    {
      key: 'createdAt',
      label: 'Created',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
  ],

  listView: {
    fields: ['name', 'artifactType', 'artifactCategory', 'claimMode', 'updatedAt'],
    // Phones keep the two leading columns; the rest is desktop
    // detail (hideOnMobile) so the table never scrolls sideways.
    columns: [
      { field: 'name' },
      { field: 'artifactType' },
      { field: 'artifactCategory', hideOnMobile: true },
      { field: 'claimMode', hideOnMobile: true },
      { field: 'updatedAt', hideOnMobile: true },
    ],
    searchableFields: ['name', 'description'],
    sortableFields: ['name', 'artifactType', 'artifactCategory', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'updatedAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    // Saved lists + the filter panel are intentionally disabled for now
    // (search + column sorting only). Filter definitions live in git history
    // (PR #369-era configs) — restore them here to re-enable both.
    filters: [],
    actions: {
      create: { enabled: true },
      view: { enabled: true },
      edit: { enabled: true },
      delete: { enabled: true },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Artifact Details',
        description: 'Core artifact configuration',
        fields: ['artifactType', 'artifactCategory', 'claimMode', 'targetCount'],
        defaultExpanded: true,
      },
      {
        title: 'Description',
        description: 'Artifact flavor text',
        fields: ['description'],
        defaultExpanded: true,
      },
      {
        title: 'Discovery Settings',
        description: 'Radius and effect configuration',
        fields: ['detectionRadius', 'claimRadius', 'effectDuration', 'effectValue'],
        defaultExpanded: true,
      },
      {
        title: 'Gate Challenge',
        description: 'Optional question before claiming',
        fields: ['gateEnabled', 'gateClue', 'gateAnswer'],
        defaultExpanded: true,
      },
      {
        title: 'Media',
        description: 'Artifact image',
        fields: ['imageUrl'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'Artifact Details',
        description: 'Basic information about the artifact',
        fields: ['name', 'artifactType', 'artifactCategory', 'description'],
        columns: 2,
      },
      {
        title: 'Discovery & Effect',
        description: 'How teams find and use this artifact',
        fields: ['detectionRadius', 'claimRadius', 'effectDuration', 'effectValue', 'targetCount', 'claimMode'],
        columns: 2,
      },
      {
        title: 'Gate Challenge',
        description: 'Optional question teams must answer before claiming',
        fields: ['gateEnabled', 'gateClue', 'gateAnswer'],
        columns: 1,
      },
      {
        title: 'Media',
        description: 'Visual for the artifact',
        fields: ['imageUrl'],
        columns: 1,
      },
    ],
    validation: {
      name: { required: true, min: 1, max: 200 },
      artifactType: { required: true },
      artifactCategory: { required: true },
    },
  },
};
